/****************
 * What is actually happening on this page, in numbers.
 *
 * Rendered only with ?debug=1 in the URL. It exists because three rounds of
 * "the dashboard is slow" could not distinguish between a query that never
 * fires, one that is still in flight, and one that failed and gave up — the
 * tiles rendered $0.00 for all three, and neither of us could see which.
 *
 * It calls the same hooks the page does. React Query dedupes by key, so this
 * adds no requests; it reads the state that already exists.
 *
 *   https://app.copyfi.ai/?debug=1
 *
 * 2026-09-03.
 ****************/

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSessions } from '@/hooks/useSessions';
import { useNotifications } from '@/hooks/useNotifications';

function ms(from: number) {
  return `${((performance.now() - from) / 1000).toFixed(1)}s`;
}

function Row({
  label,
  status,
  fetchStatus,
  count,
  error,
  firstDataAt,
  mountedAt,
}: {
  label: string;
  status: string;
  fetchStatus: string;
  count: number | null;
  error: unknown;
  firstDataAt: number | null;
  mountedAt: number;
}) {
  const tone =
    status === 'error' ? 'text-danger' : status === 'success' ? 'text-success' : 'text-warning';
  return (
    <div className="border-t border-border/40 pt-1.5 mt-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">{label}</span>
        <span className={tone}>
          {status}
          {fetchStatus !== 'idle' ? ` · ${fetchStatus}` : ''}
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {count === null ? 'no data yet' : `${count} rows`}
        {' · '}
        {firstDataAt === null ? `waiting ${ms(mountedAt)}` : `arrived after ${((firstDataAt - mountedAt) / 1000).toFixed(1)}s`}
      </div>
      {error ? (
        <div className="text-[11px] text-danger break-words mt-0.5">
          {String((error as Error)?.message ?? error)}
        </div>
      ) : null}
    </div>
  );
}

export function QueryDebugPanel() {
  const { user } = useAuth();
  const sessions = useSessions(false);
  const notifications = useNotifications();

  const mountedAt = useRef(performance.now()).current;
  const [, tick] = useState(0);
  const userAt = useRef<number | null>(null);
  const sessionsAt = useRef<number | null>(null);
  const notificationsAt = useRef<number | null>(null);

  if (user?.id && userAt.current === null) userAt.current = performance.now();
  if (sessions.data && sessionsAt.current === null) sessionsAt.current = performance.now();
  if (notifications.data && notificationsAt.current === null) notificationsAt.current = performance.now();

  // Re-render once a second so "waiting 12.0s" keeps counting while nothing
  // resolves — a frozen number is the symptom being investigated.
  useEffect(() => {
    const iv = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[330px] rounded-xl border border-border bg-background/95 backdrop-blur p-3 text-xs shadow-xl">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold">Loading diagnostics</span>
        <span className="text-[11px] text-muted-foreground">?debug=1</span>
      </div>

      <div className="border-t border-border/40 pt-1.5 mt-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium">Signed in (Clerk)</span>
          <span className={user?.id ? 'text-success' : 'text-warning'}>
            {user?.id ? 'yes' : 'not yet'}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {userAt.current === null
            ? `waiting ${ms(mountedAt)} — every query below is blocked until this resolves`
            : `resolved after ${((userAt.current - mountedAt) / 1000).toFixed(1)}s`}
        </div>
      </div>

      <Row
        label="Sessions"
        status={sessions.status}
        fetchStatus={sessions.fetchStatus}
        count={sessions.data ? sessions.data.length : null}
        error={sessions.error}
        firstDataAt={sessionsAt.current}
        mountedAt={mountedAt}
      />
      <Row
        label="Notifications"
        status={notifications.status}
        fetchStatus={notifications.fetchStatus}
        count={notifications.data ? notifications.data.length : null}
        error={notifications.error}
        firstDataAt={notificationsAt.current}
        mountedAt={mountedAt}
      />

      <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
        Times are from when this page mounted. &ldquo;pending + fetching&rdquo; means the request is
        in flight; &ldquo;pending + idle&rdquo; means it has not been sent at all.
      </p>
    </div>
  );
}
